"use client";
import React, { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "#contact" },
];

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Lock body scroll when mobile menu is open */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 px-4 sm:px-6 py-4 transition-all duration-300 ${
          scrolled ? "py-3" : ""
        }`}
      >
        <div className="max-w-6xl mx-auto">
          <div
            className={`flex items-center justify-between rounded-full px-6 py-3 transition-all duration-300 ${
              scrolled
                ? "backdrop-blur-xl bg-white/70 border border-white/40 shadow-soft"
                : "backdrop-blur-md bg-white/40 border border-white/30"
            }`}
          >
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2" onClick={closeMenu}>
              <div
                className="relative flex items-center justify-center rounded-full overflow-hidden shadow-soft"
                style={{ width: 32, height: 32 }}
              >
                <Image
                  src="/assets/images/card.jpg"
                  alt="TarotArtist Logo"
                  fill
                  className="object-cover object-[center_30%]"
                  sizes="32px"
                />
              </div>
              <span className="font-display font-semibold text-foreground text-base">
                TarotArtist
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((l) => (
                <a key={l.href} href={l.href} className="nav-link">
                  {l.label}
                </a>
              ))}
            </nav>

            {/* Desktop CTA */}
            <a
              href="https://wa.me/919993297909?text=Hi%2C%20I%20want%20to%20book%20a%20tarot%20reading%20session."
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex btn-gold px-5 py-2 rounded-full text-sm"
            >
              Book
            </a>

            {/* Hamburger */}
            <button
              className="md:hidden flex flex-col justify-center items-center w-8 h-8 gap-[5px] relative z-[60]"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <span
                className={`block w-5 h-[2px] bg-foreground rounded-full transition-all duration-300 ${
                  menuOpen ? "rotate-45 translate-y-[7px]" : ""
                }`}
              />
              <span
                className={`block w-5 h-[2px] bg-foreground rounded-full transition-all duration-300 ${
                  menuOpen ? "opacity-0" : ""
                }`}
              />
              <span
                className={`block w-5 h-[2px] bg-foreground rounded-full transition-all duration-300 ${
                  menuOpen ? "-rotate-45 -translate-y-[7px]" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-[450ms] md:hidden ${
          menuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        style={{ backdropFilter: menuOpen ? "blur(6px)" : "blur(0px)", background: "rgba(61,52,47,0.3)" }}
        onClick={closeMenu}
      />

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-[80px] left-4 right-4 z-50 rounded-2xl p-8 glass shadow-soft md:hidden transition-all duration-[450ms] cubic-bezier(0.16,1,0.3,1) ${
          menuOpen
            ? "opacity-100 translate-y-0 scale-100"
            : "opacity-0 -translate-y-[30px] scale-95 pointer-events-none"
        }`}
      >
        <nav className="flex flex-col gap-6">
          {navLinks.map((l, i) => (
            <a
              key={l.href}
              href={l.href}
              className="nav-link text-lg"
              onClick={closeMenu}
              style={{
                transitionDelay: menuOpen ? `${0.1 + i * 0.08}s` : "0s",
                opacity: menuOpen ? 1 : 0,
                transform: menuOpen ? "translateY(0)" : "translateY(12px)",
                transition: "opacity 0.4s ease, transform 0.4s ease",
              }}
            >
              {l.label}
            </a>
          ))}
          <a
            href="https://wa.me/919827128999?text=Hi%2C%20I%20want%20to%20book%20a%20tarot%20reading%20session."
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold px-6 py-3 rounded-full text-center text-sm mt-2"
            onClick={closeMenu}
            style={{
              transitionDelay: menuOpen ? `${0.1 + navLinks.length * 0.08}s` : "0s",
              opacity: menuOpen ? 1 : 0,
              transform: menuOpen ? "translateY(0)" : "translateY(12px)",
              transition: "opacity 0.4s ease, transform 0.4s ease",
            }}
          >
            Book Your Reading
          </a>
        </nav>
      </div>
    </>
  );
};

export default Header;